// src/app/components/deep-signal/deep-signal-content.ts
//
// Per-customer managed-service content for Deep Signal: the Blueprint and the
// Key Insights cards, plus the per-customer Blueprint-tab toggle.
//
// Why this file exists
// --------------------
// Key Insights and the Blueprint were previously hardcoded to a single client's
// content (Sunbit) and rendered for every customer, which leaked one client's
// material into another's view. This file makes the content per-customer and
// gives one resolver, resolveCustomerContent(), that every consumer reads from.
//
// Plug-in-later seam
// ------------------
// resolveCustomerContent() reads the backend payload FIRST and only falls back
// to the config in this file when the backend has not provided content. Both
// the blueprint and key_insights editor backends have shipped: the run payload
// carries `blueprint`, `blueprint_is_curated`, `key_insights`,
// `key_insights_is_curated`, and `blueprint_enabled`, and admin-curated values
// win automatically here with no change to any component. The config in this
// file is now the fallback for customers with no backend-provided content
// (e.g. Sunbit/Iterate keep their curated-in-code content). The shapes here are
// exactly the shapes the backend returns, so it remains a drop-in swap.
//
// No-blank guarantee
// ------------------
// Sunbit always resolves to its full content below, so Sunbit can never blank,
// regardless of backend state.

import type { DeepSignalData, Blueprint, KeyInsight } from '@/api/deep-signal-api';
import { HARDCODED_BLUEPRINT } from './fixtures';

export interface CustomerContent {
  blueprintEnabled: boolean;
  blueprint: Blueprint | null;
  keyInsights: KeyInsight[];
}

interface ContentEntry extends CustomerContent {
  // EXACT customer identifiers (lowercased, trimmed brand names) this bundled
  // content belongs to. Matching is full-string equality, never substring, so a
  // brand that merely CONTAINS "sunbit" or "iterate" can never receive another
  // client's material. List every exact DB brand_name variant explicitly.
  match: string[];
}

// ───────────────────────── Sunbit · Key Insights ───────────────────────────
// The 2026-07-27 re-measurement (May baseline vs July re-measure). Every number
// here is the canonical Compare Runs value for the run (report #46), so the
// in-product cards tell the same story the client's report does. These replace
// the demo-era fixtures Sunbit shipped with. `confidence` figures are editorial
// placeholders for the card UI, not computed statistics. The "next step" copy
// lives in `aiReasoning` (same mapping the Iterate set uses).

const SUNBIT_KEY_INSIGHTS: KeyInsight[] = [
  {
    id: 'ai-choosing-sunbit-more',
    delta: '+3.6pt',
    deltaType: 'positive',
    title: 'AI is choosing Sunbit more.',
    summary: 'Primary recommendation rose from 18.1% to 21.7% while reach held steady.',
    body: 'The re-measure repeated the May baseline exactly: same questions, same models, same grading. Sunbit appears in answers at nearly the same rate as before (63.2 to 64.2), but converts those appearances into primary recommendations far more often: in May, 29% of answers that mentioned Sunbit made it the primary pick; now 34% do. Recommendation conversion, not mention volume, is where this cycle moved.',
    confidence: 90,
    sources: 'Headline funnel · May baseline vs July re-measure',
    timeAgo: 'July 2026 re-measurement',
    aiReasoning: 'The next content wave should protect this by continuing to lead answers with specific verifiable claims, which is the pattern that converts.',
  },
  {
    id: 'web-model-moved-first',
    delta: '+Web',
    deltaType: 'positive',
    title: 'The live-web model moved first, as expected.',
    summary: 'Perplexity improved at every funnel stage this cycle.',
    body: 'Perplexity, which reads the live web on every answer, rose on every metric: mention 51.6 to 58.8, top-5 49.2 to 54.4, primary recommendation 11.2 to 17.6. This is the expected order of movement: web-reading AI reflects new content within weeks, while training-based knowledge follows over months. Perplexity is the leading indicator for whether published content is landing.',
    confidence: 88,
    sources: 'Per-model breakdown · Perplexity · May baseline vs July re-measure',
    timeAgo: 'July 2026 re-measurement',
    aiReasoning: 'Keep feeding structured, crawlable, claim-led content and watch Perplexity for the next lift before expecting the memory-based models to follow.',
  },
  {
    id: 'claims-ticked-down',
    delta: 'Steady',
    deltaType: 'info',
    title: 'A few claims ticked down, which is normal at this stage.',
    summary: 'Patient fees and patient approval slipped slightly, inside the noise range.',
    body: 'AI answers vary naturally between measurements, and these moves sit inside that normal range. Claims also compete for limited space within an answer: as newly published content pushes some claims forward, others get fewer sentences even when nothing about them changed. A dip matters when it repeats across cycles or clears the noise range. None do yet.',
    confidence: 85,
    sources: 'Capability claims · Patient fees & patient approval · May baseline vs July re-measure',
    timeAgo: 'July 2026 re-measurement',
    aiReasoning: 'Give the patient-fees claim a lead answer of its own next wave rather than a passing mention, and re-check both claims on the next cycle before reading anything directional.',
  },
  {
    id: 'lowest-merchant-fees',
    delta: 'Open',
    deltaType: 'info',
    title: 'Lowest merchant fees: room to grow.',
    summary: 'Added at Sunbit\'s request and scored on both runs: 11.5 in May, 12.0 now.',
    body: 'Sunbit asked us to add this claim to the measurement. To give it a real starting line, the original May responses were re-scored against it, so it tracks from day one: 11.5 in May, 12.0 now. AI gives Sunbit credit for simple pricing but rarely for the lowest fees, and no published answer leads with this claim yet.',
    confidence: 80,
    sources: 'Capability claims · Lowest merchant fees · May baseline vs July re-measure',
    timeAgo: 'July 2026 re-measurement',
    aiReasoning: 'The clearest place to gain ground next: a dedicated answer that leads with the lowest-fees claim.',
  },
];

// ───────────────────────── Iterate · Key Insights ──────────────────────────
// Generated from the Day 11 re-measurement (Run 14, baseline vs Day 11).
// Numbers are pulled directly from that run's per-model breakdown, headline
// funnel, and capability-claim scores. The `confidence` figures are editorial
// placeholders for the card UI, not computed statistics; set them as you like.

const ITERATE_KEY_INSIGHTS: KeyInsight[] = [
  {
    id: 'web-models-carrying',
    delta: '+Web',
    deltaType: 'positive',
    title: 'The live-web models are carrying the lift.',
    summary: 'Perplexity and ChatGPT Web account for almost all the movement',
    body: 'Across the four models, the gains concentrate in the two that search the live web. Perplexity rose on every metric (mention 28.4 to 46.0, top-5 26.8 to 44.8, primary recommendation 1.2 to 7.2, named #1 1.2 to 6.0). ChatGPT Web moved up clearly on mention (6.4 to 12.4) and top-5 (0.0 to 12.0). This is the expected order this early: web-search tools reflect new content within days, while training-based knowledge updates over months.',
    confidence: 90,
    sources: 'Per-model breakdown · Baseline vs Day 11 · Run 14',
    timeAgo: 'Day 11 re-measurement',
    aiReasoning: 'Because the web models update first, they are the leading indicator for this campaign. Keep feeding structured, crawlable content and watch Perplexity and ChatGPT Web for the next lift before expecting the training-based models to follow.',
  },
  {
    id: 'named-number-one',
    delta: '+3.6pt',
    deltaType: 'positive',
    title: 'Iterate went from almost never the #1 pick to about 1 in 25 answers.',
    summary: 'Named #1 rose from 0.4 to 4.0, and it shows up across multiple models',
    body: 'The headline named-#1 rate moved from 0.4 to 4.0. It is not isolated to a single model: Perplexity reached 6.0 and ChatGPT Web 1.6, with Claude also contributing. Being chosen as the single top recommendation is the hardest step in the funnel, so movement here this early is the strongest signal in the set.',
    confidence: 85,
    sources: 'Headline funnel · Baseline vs Day 11 · Run 14',
    timeAgo: 'Day 11 re-measurement',
    aiReasoning: 'The next step is converting more mentions into primary recommendations. The content that earns a #1 pick tends to be specific, comparative, and easy to quote, so prioritize pages that state Iterate differentiated capabilities plainly.',
  },
  {
    id: 'capabilities-credited',
    delta: '+3 claims',
    deltaType: 'positive',
    title: 'AI is starting to credit the three capabilities Wave 1 targeted.',
    summary: 'The capability claims you published the most about each rose 3 to 4x',
    body: 'All seven tracked capability claims increased, and the three with the largest gains are the ones Wave 1 content most directly addressed: survey responses writing back to Braze and Iterable (0.07 to 0.27), activating segments and campaigns from survey responses (0.05 to 0.20), and native delivery through the existing Braze or Iterable SDK (0.05 to 0.17). These are off low bases, so read the direction and the ranking as the signal and treat the magnitudes as early.',
    confidence: 80,
    sources: 'Capability claims · All models · Run 14',
    timeAgo: 'Day 11 re-measurement',
    aiReasoning: 'The content moved exactly the claims it targeted, which is the cleanest evidence the approach is working. Reinforce these three with more structured, citable sources, then extend the same pattern to the weaker claims (targeting, mobile, voice of customer).',
  },
  {
    id: 'training-models-flat',
    delta: 'Expected',
    deltaType: 'info',
    title: 'The training-based models are flat so far, which is expected this early.',
    summary: 'Static ChatGPT stayed at zero; Claude moved within noise',
    body: 'Static ChatGPT stayed at 0.0 across every metric, and Claude moved only slightly (mention 11.2 to 14.0) with its primary recommendation dipping 7.6 to 6.8. On about 250 calls per model, that dip is within run-to-run noise, not a real decline. Training-based knowledge does not change in 11 days, so flatness here is the expected pattern, not a problem.',
    confidence: 88,
    sources: 'Per-model breakdown · Static ChatGPT and Claude · Run 14',
    timeAgo: 'Day 11 re-measurement',
    aiReasoning: 'Do not over-read the Claude dip. The checkpoint that matters for these models is months out, when training-based knowledge has had time to absorb the new content. The web models moving first is the leading sign that the rest can follow.',
  },
];

// ───────────────────────── Iterate · Blueprint ─────────────────────────────
// Iterate Reasoning Path Blueprint (Frontier, Apr 2026). Wave 1 to 3 mapped
// into the Sunbit shape: tactic name (title) + description only; the strategy
// and owner columns from the source doc are intentionally dropped. Source:
// Iterate_Reasoning Path Blueprint_Frontier_04.28.26 (Google Doc).

const ITERATE_BLUEPRINT: Blueprint = {
  produced_at: 'April 2026',
  waves: [
    {
      number: 1,
      title: 'Foundation',
      subtitle: '4 interventions',
      velocity_label: 'Days to weeks',
      interventions: [
        { id: 'W1-01', title: 'G2 Profile Optimization', description: 'Full About section rewritten from Blueprint language. All 7 feature tags set. Q&A section populated with mechanism-first answers (why it works). Review response templates drafted.', effort: 'LOW', signal: '' },
        { id: 'W1-02', title: 'Capterra and GetApp Profiles', description: 'Same Blueprint language adapted for each platform. Feature tags, category placement, long-form description. Parallel to G2.', effort: 'LOW', signal: '' },
        { id: 'W1-03', title: 'Product Hunt Listing', description: 'Feature descriptions updated from Blueprint. SDK and integration highlights. Tagline sharpened to CIM category language.', effort: 'LOW', signal: '' },
        { id: 'W1-04', title: 'llms.txt', description: 'Structured file at iteratehq.com/llms.txt. One block per reasoning variable. Mechanism-first language (why it works). Formatted for AI crawler consumption, bypassing JS rendering constraints entirely.', effort: 'LOW', signal: '' },
      ],
    },
    {
      number: 2,
      title: 'Comparison and Outreach',
      subtitle: '6 interventions',
      velocity_label: 'Days to weeks',
      interventions: [
        { id: 'W2-01', title: 'Comparison Pages', description: 'One dedicated page per competitor (Survicate, Typeform, Sprig, SurveyMonkey, Qualtrics). Same template: positioning statement, 7-variable comparison table drawn from Blueprint, and use-case fit. Pages written from a single template; inputs are competitor name and variable deltas.', effort: 'MEDIUM', signal: '' },
        { id: 'W2-02', title: 'Roundup Outreach', description: 'Identify 10 roundup articles in target categories (best survey tools, best mobile research, best in-app feedback). Draft personalized outreach to each author requesting Iterate inclusion, including a ready-made paragraph with mechanism-first language they can paste directly.', effort: 'HIGH', signal: '' },
        { id: 'W2-03', title: 'Integration Directory Listings', description: 'Partner and integration page listings for Iterable and Braze, with Blueprint language adapted for a technical audience.', effort: 'MEDIUM', signal: '' },
        { id: 'W2-04', title: 'GitHub README', description: 'Repository README rewritten from Blueprint language. SDK-specific capability descriptions mapped to each reasoning variable. Written for technical evaluators searching for mobile survey SDK options, not for marketing audiences.', effort: 'MEDIUM', signal: '' },
        { id: 'W2-05', title: 'Community Placements', description: 'LinkedIn: 5 long-form articles from Iterate team member accounts. Quora: structured, non-promotional answers on high-traffic survey tool and mobile feedback questions. YouTube: short how-to walkthroughs demonstrating integration capabilities (Braze writeback, SDK delivery, campaign activation). Each piece maps to 1 to 2 reasoning variables.', effort: 'MEDIUM', signal: '' },
        { id: 'W2-06', title: 'Zenodo Research Note', description: 'Short-form research note documenting the CIM category and Iterate\'s architectural approach to continuous in-product research. Mechanism-first, published with a citable DOI as an independent academic record.', effort: 'HIGH', signal: '' },
      ],
    },
    {
      number: 3,
      title: 'Owned Content Scale',
      subtitle: '4 interventions',
      velocity_label: 'Weeks to months',
      interventions: [
        { id: 'W3-01', title: 'Template Pages', description: 'Use-case pages (Mobile NPS, post-purchase feedback, feature adoption surveys, onboarding feedback, user research recruitment, plus 5 more). Each page: use-case description, the Iterate capability mapped to it from Blueprint, and a product signup CTA. All generated from a single template.', effort: 'MEDIUM', signal: '' },
        { id: 'W3-02', title: 'CIM Category Pillar', description: 'Definitive long-form page establishing the Customer Insights Manager category with Iterate as originator. Written for AI-retrievable language. Internally linked site-wide.', effort: 'HIGH', signal: '' },
        { id: 'W3-03', title: 'Third-Party Case Study', description: 'Mechanism-first case study published on a practitioner platform or Medium. A real use case with Iterate-specific capability language. Not a press release.', effort: 'HIGH', signal: '' },
        { id: 'W3-04', title: 'FAQ Blocks (Optional)', description: 'A FAQ block of 5 questions targeting benchmark prompt categories, to add to deployment if desired. Uses the same format as the 25-prompt battery.', effort: 'LOW', signal: '' },
      ],
    },
  ],
};

// ───────────────────────────── Registry ────────────────────────────────────

const ENTRIES: ContentEntry[] = [
  {
    match: ['sunbit', 'sunbit - dental (merchant)'],
    blueprintEnabled: true,
    blueprint: HARDCODED_BLUEPRINT as Blueprint,
    keyInsights: SUNBIT_KEY_INSIGHTS,
  },
  {
    match: ['iterate'],
    blueprintEnabled: true,
    blueprint: ITERATE_BLUEPRINT,
    keyInsights: ITERATE_KEY_INSIGHTS,
  },
];

const EMPTY: CustomerContent = {
  blueprintEnabled: false,
  blueprint: null,
  keyInsights: [],
};

function configFor(brandName: string | undefined): ContentEntry | null {
  if (!brandName) return null;
  const q = brandName.trim().toLowerCase();
  return ENTRIES.find((e) => e.match.some((m) => q === m)) ?? null;
}

// Single source of truth for Deep Signal managed-service content. Backend
// payload wins when present; per-customer config is the fallback; safe-empty is
// the default so an unconfigured customer shows no Blueprint tab and no Key
// Insights box rather than another client's content.
// Brand-name-only resolver for surfaces that do not have the Deep Signal run
// payload in scope (e.g. the Brand Monitoring Blueprint hand-off card). Returns
// the per-customer config content. The backend-first path in
// resolveCustomerContent() still wins anywhere the run payload is available.
export function resolveCustomerContentByBrand(brandName: string | undefined): CustomerContent {
  const cfg = configFor(brandName);
  if (!cfg) return EMPTY;
  return {
    blueprintEnabled: cfg.blueprintEnabled,
    blueprint: cfg.blueprint,
    keyInsights: cfg.keyInsights,
  };
}

export function resolveCustomerContent(apiData: DeepSignalData | null): CustomerContent {
  if (!apiData) return EMPTY;
  const cfg = configFor(apiData.customer.brand_name);

  // Treat an empty backend blueprint (waves: 0) as "not provided" so it does not
  // override the config. Same for an empty key_insights array.
  const apiBlueprint =
    apiData.blueprint && apiData.blueprint.waves && apiData.blueprint.waves.length > 0
      ? apiData.blueprint
      : null;
  const apiKeyInsights =
    apiData.key_insights && apiData.key_insights.length > 0 ? apiData.key_insights : null;

  // Blueprint precedence: an admin-curated copy from the backend wins
  // (blueprint_is_curated), then the per-customer config in this file
  // (Sunbit, Iterate keep their curated-in-code content), then the generated
  // Stage 9 draft from the payload (St Mary before curation).
  const curatedApiBlueprint = apiData.blueprint_is_curated ? apiBlueprint : null;
  const blueprint = curatedApiBlueprint ?? cfg?.blueprint ?? apiBlueprint ?? null;

  return {
    blueprintEnabled: apiData.blueprint_enabled ?? cfg?.blueprintEnabled ?? false,
    blueprint,
    keyInsights: apiKeyInsights ?? cfg?.keyInsights ?? [],
  };
}
